import { useState } from 'react'
import { useBreadcrumb } from '../hooks/useBreadcrumb'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { FaMapMarkerAlt, FaSearch, FaTruck, FaCheckCircle, FaPlane, FaShip, FaWarehouse, FaBox } from 'react-icons/fa'
import { trackingService } from '../services'
import { Card, Button, Input, PageHeader, LoadingSpinner } from '../components/common'
import { formatDateTime } from '../utils/datetime'

const eventIcons = {
  'Created': FaBox,
  'In Warehouse': FaWarehouse,
  'Shipped': FaTruck,
  'In Transit': FaPlane,
  'Customs': FaShip,
  'Delivered': FaCheckCircle,
}

export default function TrackingPage() {
  const [trackingResult, setTrackingResult] = useState(null)
  const [isLoading, setIsLoading] = useState(false)

  const { register, handleSubmit, formState: { errors } } = useForm()

  const onSubmit = async (data) => {
    setIsLoading(true)
    try {
      const result = await trackingService.getTrackingInfo(data.trackingNumber)
      if (result?.status === 'success') {
        setTrackingResult(result)
      } else {
        toast.error('未找到运单信息')
        setTrackingResult(null)
      }
    } catch (error) {
      toast.error(error.message || '查询失败')
      setTrackingResult(null)
    } finally {
      setIsLoading(false)
    }
  }

  // 本组件曾被散货的 /lcl-tracking 复用，故原先按 pathname 分两种面包屑；
  // 该路由已随散货板块注释下线（App.jsx），恢复时把它加回 navigation.js 即可，这里不用再判。
  const breadcrumb = useBreadcrumb()

  return (
    <div>
      <PageHeader
        icon={FaMapMarkerAlt}
        title="物流轨迹查询"
        subtitle="输入运单号，实时追踪您的货物状态"
        breadcrumb={breadcrumb}
      />

      <div className="container py-8">
        <div className="max-w-3xl mx-auto">
          {/* Search Form */}
          <Card className="mb-8">
            <Card.Body>
              {/* 小屏竖排，与集运报名/验货查询/集运订单的查询条同一套处理 */}
              <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <div className="flex-1 min-w-0">
                  <Input
                    placeholder="请输入运单号 / 报名单号"
                    size="lg"
                    error={errors.trackingNumber?.message}
                    {...register('trackingNumber', { required: '请输入运单号' })}
                  />
                </div>
                <Button type="submit" size="lg" loading={isLoading} icon={<FaSearch />} className="w-full sm:w-auto shrink-0">
                  查询
                </Button>
              </form>
            </Card.Body>
          </Card>

          {/* Results */}
          {isLoading ? (
            <Card>
              <Card.Body>
                <div className="flex flex-col items-center py-12">
                  <LoadingSpinner size="lg" />
                  <p className="mt-4 text-gray-500">正在查询中...</p>
                </div>
              </Card.Body>
            </Card>
          ) : trackingResult ? (
            <div className="space-y-6">
              {/* Shipment Info */}
              <Card>
                <Card.Header>
                  <h5 className="font-semibold">运单信息</h5>
                </Card.Header>
                <Card.Body>
                  <div className="grid grid-cols-fluid-xs gap-6">
                    <div>
                      <p className="text-sm text-gray-500 mb-1">运单号</p>
                      <p className="font-semibold">{trackingResult.tracking_number}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 mb-1">当前状态</p>
                      <p className="font-semibold text-primary-600">{trackingResult.current_status}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 mb-1">目的地</p>
                      <p className="font-semibold">{trackingResult.destination}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 mb-1">预计送达</p>
                      <p className="font-semibold">
                        {trackingResult.estimated_delivery || '-'}
                      </p>
                    </div>
                  </div>
                </Card.Body>
              </Card>

              {/* Timeline */}
              <Card>
                <Card.Header>
                  <h5 className="font-semibold">物流轨迹</h5>
                </Card.Header>
                <Card.Body>
                  {trackingResult.events?.length > 0 ? (
                    <div className="relative">
                      {/* Timeline line */}
                      <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200" />

                      <div className="space-y-6">
                        {trackingResult.events.map((event, index) => {
                          const Icon = eventIcons[event.event_type] || FaMapMarkerAlt
                          const isLatest = index === 0

                          return (
                            <div key={index} className="relative flex gap-4">
                              <div className={`relative z-10 w-12 h-12 rounded-full flex items-center justify-center ${
                                isLatest
                                  ? 'bg-primary-600 text-white'
                                  : 'bg-white border-2 border-gray-200 text-gray-400'
                              }`}>
                                <Icon className={isLatest ? 'text-lg' : 'text-base'} />
                              </div>
                              <div className="flex-1 pt-2">
                                <div className="flex items-center justify-between mb-1">
                                  <h6 className={`font-medium ${isLatest ? 'text-gray-900' : 'text-gray-600'}`}>
                                    {event.event_description}
                                  </h6>
                                  <span className="text-sm text-gray-400">
                                    {formatDateTime(event.event_time, 'MM-DD HH:mm')}
                                  </span>
                                </div>
                                {event.location && (
                                  <p className="text-sm text-gray-500">
                                    <FaMapMarkerAlt className="inline mr-1" />
                                    {event.location}
                                  </p>
                                )}
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      暂无轨迹信息
                    </div>
                  )}
                </Card.Body>
              </Card>
            </div>
          ) : (
            <Card>
              <Card.Body>
                <div className="text-center py-16 text-gray-400">
                  <FaMapMarkerAlt className="text-5xl mx-auto mb-4" />
                  <p>输入运单号查询物流轨迹</p>
                </div>
              </Card.Body>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}
